import React, { useMemo, useRef, useState } from 'react';
import { RotateCcw, ArrowUp, ArrowDown } from 'lucide-react';
import { toMin, fromMin } from './plannerShared';

// Individual-meeting slots in a block's panel: one list per window (the whole block, or each
// group's part of a rotation). WHO gets a slot follows the block's "Who" (the named players, or
// the group members in a rotation) and is kept in sync by the server; here staff only set the
// ORDER — drag a name or use the arrows. Slots follow each other from the window's start. Each
// player only ever sees their own slot.

export default function SlotsEditor({ block, players, canEdit, busy, stale, onSave, onReset }) {
  const initial = () => Object.fromEntries(block.slot_windows.map(w => [
    String(w.group ?? 0),
    block.slots.filter(s => (s.group ?? 0) === (w.group ?? 0)).sort((a, b) => a.index - b.index).map(s => s.player_id),
  ]));
  const [order, setOrder] = useState(initial);
  const dragRef = useRef(null);
  const nameOf = useMemo(() => Object.fromEntries(players.map(p => [p.id, p.name])), [players]);
  const dirty = JSON.stringify(order) !== JSON.stringify(initial());

  const move = (key, from, to) => setOrder(prev => {
    const list = [...prev[key]];
    if (to < 0 || to >= list.length) return prev;
    const [x] = list.splice(from, 1);
    list.splice(to, 0, x);
    return { ...prev, [key]: list };
  });

  if (stale) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Save the block to update the slots (they follow its times, slot length and who it is for).</div>;
  }

  const total = Object.values(order).flat().length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {total === 0 && !block.slot_unplaced.length && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {block.rotation
            ? 'No slots yet: put players in Group 1 / Group 2 for this day (Groups button in the day header).'
            : 'No slots yet: pick the players under "Who" → Named players, or a group.'}
        </div>
      )}
      {block.slot_windows.map(w => {
        const key = String(w.group ?? 0);
        const list = order[key] || [];
        return (
          <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 2 }}>
              {w.group ? `Group ${w.group} · ` : ''}{w.start}–{w.end} · {list.length} of {w.capacity} slots used
            </div>
            {list.map((pid, i) => {
              const start = toMin(w.start) + i * block.slot_minutes;
              return (
                <div key={pid}
                  draggable={canEdit}
                  onDragStart={() => { dragRef.current = { key, i }; }}
                  onDragOver={e => { if (dragRef.current?.key === key) e.preventDefault(); }}
                  onDrop={() => { const d = dragRef.current; dragRef.current = null; if (d && d.key === key) move(key, d.i, i); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '2px 0', cursor: canEdit ? 'grab' : 'default' }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 12, width: 92, color: 'var(--text-secondary)' }}>
                    {fromMin(start)}–{fromMin(start + block.slot_minutes)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>{nameOf[pid] || `Player #${pid}`}</span>
                  {canEdit && (
                    <>
                      <button type="button" className="pl-btn" style={{ minHeight: 24, padding: '1px 5px' }} aria-label={`Move ${nameOf[pid] || 'player'} earlier`} disabled={i === 0} onClick={() => move(key, i, i - 1)}><ArrowUp size={12} /></button>
                      <button type="button" className="pl-btn" style={{ minHeight: 24, padding: '1px 5px' }} aria-label={`Move ${nameOf[pid] || 'player'} later`} disabled={i === list.length - 1} onClick={() => move(key, i, i + 1)}><ArrowDown size={12} /></button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      {block.slot_unplaced.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--pl-err)' }}>
          No room for: {block.slot_unplaced.map(id => nameOf[id] || `#${id}`).join(', ')}. Make the block longer or the slots shorter.
        </div>
      )}
      {canEdit && total > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" className="pl-btn pl-btn--primary" disabled={busy || !dirty}
            onClick={() => onSave(block.slot_windows.map(w => ({ group: w.group, player_ids: order[String(w.group ?? 0)] || [] })))}>Save order</button>
          <button type="button" className="pl-btn" disabled={busy} onClick={onReset} title="Back to the order the players were picked in (or squad-number order per group)"><RotateCcw size={14} /> Reset order</button>
        </div>
      )}
    </div>
  );
}
